import type { QueryClient } from '@tanstack/react-query';

/**
 * QueryClient registry for TanStack Query integration
 *
 * This module provides a registry pattern for sharing the application's QueryClient
 * instance across the aw-api-clients package. The QueryClient is created and configured
 * by the application, then registered here for use in API wrapper functions.
 *
 * This follows the dependency injection pattern recommended for library code.
 *
 * @see https://tanstack.com/query/latest/docs/framework/react/guides/advanced-ssr
 */

let registeredQueryClient: QueryClient | null = null;

/**
 * Register the application's QueryClient instance.
 * Must be called once at app startup before using any query wrappers.
 *
 * @param client - The QueryClient instance created by the application
 * @throws Error if called multiple times
 */
export function setQueryClient(client: QueryClient): void {
  if (registeredQueryClient !== null) {
    throw new Error('QueryClient already registered. setQueryClient() should only be called once.');
  }
  registeredQueryClient = client;
}

/**
 * Get the registered QueryClient instance.
 *
 * @returns The registered QueryClient instance
 * @throws Error if called before setQueryClient()
 * @internal
 */
export function getQueryClient(): QueryClient {
  if (registeredQueryClient === null) {
    throw new Error('QueryClient not registered. Call setQueryClient() at app startup.');
  }
  return registeredQueryClient;
}

/**
 * Reset the registered QueryClient instance.
 *
 * @internal
 */
export function resetQueryClient(): void {
  registeredQueryClient = null;
}
